import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EmailIcon from '@mui/icons-material/Email';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useAuth } from '../../contexts/AuthContext';

export default function AppLayout() {
  const navigate = useNavigate();
  const { host, logout } = useAuth();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login', { replace: true });
  };

  // First letter of the user's name for the avatar
  const initials = host?.name?.charAt(0).toUpperCase() ?? '?';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top bar */}
      <AppBar position="sticky" elevation={1} color="default" sx={{ bgcolor: 'background.paper' }}>
        <Toolbar>
          <Typography
            variant="h6"
            fontWeight={700}
            color="primary"
            sx={{ flexGrow: 1, cursor: 'pointer' }}
            onClick={() => navigate('/dashboard')}
          >
            TimeToRSVP
          </Typography>

          {/* Admin link - desktop only (visible as button) */}
          {host?.isPlatformAdmin && (
            <Button
              startIcon={<AdminPanelSettingsIcon />}
              onClick={() => navigate('/admin')}
              sx={{
                mr: 1,
                minHeight: 44,
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              Admin
            </Button>
          )}

          {/* User menu */}
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
            {host?.name}
          </Typography>

          <IconButton onClick={handleMenuOpen} size="small">
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 16 }}>
              {initials}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {host?.email}
              </Typography>
            </MenuItem>
            {host?.isPlatformAdmin && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate('/admin');
                }}
                sx={{ display: { xs: 'flex', sm: 'none' } }}
              >
                <ListItemIcon><AdminPanelSettingsIcon fontSize="small" /></ListItemIcon>
                Admin Panel
              </MenuItem>
            )}
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/account/profile');
              }}
            >
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              My Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/account/notifications');
              }}
            >
              <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
              Notifications
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/account/email-usage');
              }}
            >
              <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
              Email Usage
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/account/billing');
              }}
            >
              <ListItemIcon><ReceiptLongIcon fontSize="small" /></ListItemIcon>
              Billing History
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Log out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Page content */}
      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Outlet />
      </Container>
    </Box>
  );
}
